/**
 * @fileoverview The profile panels of the Active Client details.
 * @description Organisation, location, the primary and secondary contacts and the relationship
 * (owner and client-since date). When the backend masked the contacts (a Manager or Executive who
 * does not own the client) the masked values are shown as they came, under a notice. The HR Admin
 * block is its own component (ClientHrAdminSection), since it carries the resend control.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { dayOf } from '@/features/sales/lib/sales-clients.utils'
import DetailField from '@/shared/components/molecules/DetailField'

import ClientPanel from './ClientPanel'

import type { ClientContact, ClientDetails } from '@/features/sales/types/sales-client.types'

/**
 * @component
 * @param {{ contact: ClientContact; prefix: string }} props - The contact and its locator prefix.
 * @param {ClientContact} props.contact - The primary or secondary contact.
 * @param {string} props.prefix - The test-id prefix of its fields.
 */
function ContactFields({ contact, prefix }: { contact: ClientContact; prefix: string }) {
  const { t } = useTranslation()
  const d = 'sales.activeClients.detail'

  return (
    <>
      <DetailField label={t(`${d}.contactPerson`)} data-testid={`${prefix}-name`}>
        {contact.name}
      </DetailField>
      <DetailField label={t(`${d}.designation`)} data-testid={`${prefix}-designation`}>
        {contact.designation ?? '—'}
      </DetailField>
      <DetailField label={t(`${d}.email`)} data-testid={`${prefix}-email`}>
        {contact.email}
      </DetailField>
      <DetailField label={t(`${d}.phone`)} data-testid={`${prefix}-phone`}>
        {contact.phone}
      </DetailField>
      {contact.officePhone && (
        <DetailField label={t(`${d}.officePhone`)} data-testid={`${prefix}-office-phone`}>
          {contact.officePhone}
        </DetailField>
      )}
    </>
  )
}

/**
 * @component
 * @param {{ client: ClientDetails }} props - The client.
 * @param {ClientDetails} props.client - The client in full.
 */
function ClientDetailPanels({ client }: { client: ClientDetails }) {
  const { t } = useTranslation()
  const d = 'sales.activeClients.detail'
  const { organisation, location, relationship } = client

  return (
    <>
      <ClientPanel title={t(`${d}.basicInfo`)} testId="client-detail-organisation">
        <DetailField label={t(`${d}.clientId`)} data-testid="client-detail-code">
          {client.tenantCode}
        </DetailField>
        <DetailField label={t(`${d}.bankType`)} data-testid="client-detail-bank-type">
          {organisation.bankType.name}
        </DetailField>
        <DetailField label={t(`${d}.regNumber`)} data-testid="client-detail-reg-number">
          {organisation.registrationNumber ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.website`)} data-testid="client-detail-website">
          {organisation.website ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.headOffice`)} data-testid="client-detail-head-office">
          {organisation.headOfficeLocation}
        </DetailField>
        <DetailField label={t(`${d}.bankTier`)} data-testid="client-detail-tier">
          {organisation.tier.name}
        </DetailField>
        <DetailField label={t(`${d}.branches`)} data-testid="client-detail-branches">
          {organisation.branchCount}
        </DetailField>
        <DetailField label={t(`${d}.employees`)} data-testid="client-detail-employees">
          {organisation.employeeCount ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.coreBanking`)} data-testid="client-detail-core-banking">
          {organisation.coreBankingSolution?.name ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.existingMis`)} data-testid="client-detail-existing-mis">
          {organisation.existingMisSolution ?? '—'}
        </DetailField>
      </ClientPanel>

      <ClientPanel title={t(`${d}.address`)} testId="client-detail-location">
        <DetailField label={t(`${d}.address`)} data-testid="client-detail-street">
          {organisation.streetAddress ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.city`)} data-testid="client-detail-city">
          {location.city}
        </DetailField>
        <DetailField label={t(`${d}.district`)} data-testid="client-detail-district">
          {location.district ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.state`)} data-testid="client-detail-state">
          {location.state.name}
        </DetailField>
        <DetailField label={t(`${d}.pincode`)} data-testid="client-detail-pincode">
          {location.pinCode ?? '—'}
        </DetailField>
      </ClientPanel>

      <ClientPanel title={t(`${d}.primaryContact`)} testId="client-detail-primary-contact">
        {client.contactsMasked && (
          <p
            className="col-span-full text-compact-sm text-slate"
            data-testid="client-detail-masked-note"
          >
            {t('sales.activeClients.maskedNote')}
          </p>
        )}
        <ContactFields contact={client.primaryContact} prefix="client-detail-primary" />
      </ClientPanel>

      {client.secondaryContact && (
        <ClientPanel title={t(`${d}.secondaryContact`)} testId="client-detail-secondary-contact">
          <ContactFields contact={client.secondaryContact} prefix="client-detail-secondary" />
        </ClientPanel>
      )}

      <ClientPanel title={t(`${d}.coverage`)} testId="client-detail-relationship">
        <DetailField label={t(`${d}.owner`)} data-testid="client-detail-owner">
          {relationship.executive?.name ?? '—'}
        </DetailField>
        <DetailField label={t(`${d}.onboarded`)} data-testid="client-detail-onboarded">
          {dayOf(relationship.onboardedAt)}
        </DetailField>
      </ClientPanel>
    </>
  )
}

export default ClientDetailPanels
